import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import NotificationSettings from './NotificationSettings';
import Statistic from './Statistic';

const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
  const [isStatisticsOpen, setIsStatisticsOpen] = useState(false);
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const menuItems = [
    {
      icon: '📊',
      title: 'Statistics',
      description: 'View prediction results',
      href: '#',
      onClick: () => {
        setIsOpen(false);
        setIsStatisticsOpen(true);
      },
      available: true // Set true when Statistics is ready
    },
    {
      icon: '🎪',
      title: 'Challenges',
      description: 'Daily and weekly tasks',
      href: '/challenges',
      available: false // Future feature
    },
    {
      icon: '⚙️',
      title: 'Settings',
      description: 'Notifications & preferences',
      href: '#',
      onClick: () => {
        setIsOpen(false);
        setIsNotificationSettingsOpen(true);
      }
    },
    {
      icon: '📱',
      title: 'About',
      description: 'About Main-Bet',
      href: '/about',
      available: true // Future feature
    }
  ];

  return (
    <>
      {/* Hamburger Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-full bg-secondary-light dark:bg-dark-light relative"
        aria-label="Open menu"
      >
        <svg 
          className="w-5 h-5 text-gray-600 dark:text-gray-300" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4 6h16M4 12h16M4 18h16" 
          />
        </svg>
      </motion.button>

      {/* Menu Overlay and Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50"
          >
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 30 
              }}
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-800 shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Menu
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close menu"
                >
                  <svg 
                    className="w-5 h-5 text-gray-500 dark:text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-4 space-y-2">
                {menuItems.map((item, index) => (
                  <MenuItem
                    key={item.title}
                    item={item}
                    index={index}
                    onClose={() => setIsOpen(false)}
                  />
                ))}
              </div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Main-Bet v1.0
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Professional Football Predictions
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Settings Modal */}
      <NotificationSettings 
        isOpen={isNotificationSettingsOpen}
        onClose={() => setIsNotificationSettingsOpen(false)}
      />

      {/* Statistics Modal */}
      <Statistic 
        isOpen={isStatisticsOpen}
        onClose={() => setIsStatisticsOpen(false)}
      />
    </>
  );
};

// Menu Item Component
const MenuItem = ({ item, index, onClose }) => {
  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href && item.available !== false) {
      onClose();
    }
  };

  const content = (
    <div
      className={`
        flex items-center p-3 rounded-xl transition-all duration-200
        ${item.available === false 
          ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700/50' 
          : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
        }
      `}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className="text-2xl mr-3 flex-shrink-0">
        {item.icon}
      </div>

      {/* Text Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 dark:text-white text-sm">
          {item.title}
          {item.available === false && (
            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-300">
              Soon
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {item.description}
        </p>
      </div>

      {/* Arrow Icon */}
      {item.available !== false && (
        <svg 
          className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 5l7 7-7 7" 
          />
        </svg>
      )}
    </div>
  );

  // If item is available and has href, wrap in Link
  if (item.available !== false && item.href && item.href !== '#') {
    return (
      <Link to={item.href} onClick={onClose}>
        {content}
      </Link>
    );
  }

  return content;
};

export default HamburgerMenu; 